import React, { useEffect, useState } from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, Tooltip, YAxis } from "recharts";
import { Loader2, TrendingUp, TrendingDown } from "lucide-react";
import api, { formatMoney } from "../../lib/api";

const corto = (label) => (label || "").slice(0, 3).toLowerCase();

function MiniCard({ v, nombre, d, meses }) {
  const data = meses.map((m, i) => ({ mes: corto(m.label), label: m.label, actual: d.actual[i], anterior: d.anterior[i], parcial: m.parcial_dias }));
  const up = (d.var ?? 0) >= 0;
  return (
    <div className="card p-4" data-testid={`tendencia-card-${v}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold truncate">{nombre}</div>
          <div className="text-[11px] text-slate-500">12 meses: <b className="text-slate-800">{formatMoney(d.total_actual)}</b></div>
        </div>
        {d.var != null ? (
          <span className={"inline-flex items-center gap-1 text-xs font-bold rounded-full px-2 py-0.5 " + (up ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700")}
                title={`Comparando ${d.meses_comparables} meses con datos en los dos años`} data-testid={`tendencia-var-${v}`}>
            {up ? <TrendingUp size={12} /> : <TrendingDown size={12} />}{up ? "+" : ""}{String(d.var).replace(".", ",")} %
          </span>
        ) : <span className="text-[10px] text-slate-400">sin año anterior</span>}
      </div>
      <ResponsiveContainer width="100%" height={90}>
        <LineChart data={data} margin={{ top: 8, right: 4, left: 4, bottom: 0 }}>
          <XAxis dataKey="mes" tick={{ fontSize: 9 }} interval={1} tickLine={false} axisLine={false} padding={{ left: 10, right: 10 }} />
          <YAxis hide domain={[0, "auto"]} />
          <Tooltip contentStyle={{ fontSize: 11, borderRadius: 6 }} labelFormatter={(_, p) => (p?.[0]?.payload?.label || "") + (p?.[0]?.payload?.parcial ? ` (${p[0].payload.parcial} días)` : "")}
                   formatter={(val, n) => [val == null ? "—" : formatMoney(val), n]} />
          <Line dataKey="anterior" name="Año anterior" stroke="#cbd5e1" strokeWidth={2} strokeDasharray="4 3" dot={false} connectNulls />
          <Line dataKey="actual" name="Últimos 12 meses" stroke="#0f172a" strokeWidth={2} dot={{ r: 1.5 }} connectNulls />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export function TendenciaAnual({ hasta, names }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    if (hasta) api.get("/sales/employees/tendencia", { params: { hasta } }).then((r) => setD(r.data)).catch(() => setD({ empleados: {} }));
  }, [hasta]);
  if (!d) return <div className="text-slate-500 flex items-center gap-2 py-4"><Loader2 size={16} className="animate-spin" /> Cargando tendencia anual…</div>;
  const emps = Object.entries(d.empleados).filter(([, x]) => x.total_actual > 0).sort((a, b) => b[1].total_actual - a[1].total_actual);
  if (!emps.length) return null;
  const parciales = (d.meses || []).filter((m) => m.parcial_dias);
  return (
    <div className="card p-6 mb-6" data-testid="tendencia-anual">
      <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Equipo</div>
      <h3 className="font-display text-xl font-bold mt-1">Tendencia anual de facturación</h3>
      <p className="text-xs text-slate-500 mt-1 mb-4">
        Línea negra: últimos 12 meses hasta {d.meses.at(-1)?.label}. Línea gris: mismos meses del año anterior. El % compara solo los meses con datos en los dos años.
        {parciales.length > 0 && <> Meses subidos a medias: {parciales.map((m) => `${m.label} (${m.parcial_dias} días)`).join(", ")}.</>}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {emps.map(([v, x]) => <MiniCard key={v} v={v} nombre={names[v] || v} d={x} meses={d.meses} />)}
      </div>
    </div>
  );
}
